import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

import RestoreStatusTimeline from '../components/Backup/RestoreStatusTimeline';
import RestoreStatusBadge from '../components/Backup/RestoreStatusBadge';
import RestoreConfirmModal from '../components/Backup/RestoreConfirmModal';
import apiService from '../services/api';
import { Backup, RestoreOperation } from '../types/api';
import { darkModeClasses } from '../utils/darkModeClasses';
import { getApiErrorMessage } from '../constants/messages';
import Logger from '../utils/logger';

const RestorePage: React.FC = () => {
  const { t } = (useTranslation as any)('backups');
  const { deploymentId, backupId, restoreId } = useParams<{
    deploymentId: string;
    backupId?: string;
    restoreId?: string;
  }>();
  const navigate = useNavigate();

  const [backup, setBackup] = useState<Backup | null>(null);
  const [restore, setRestore] = useState<RestoreOperation | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [validating, setValidating] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // Keycloak heberges par ce PostgreSQL. CNPG restaure un CLUSTER, pas une base :
  // sans cet avertissement, restaurer une base applicative ramene aussi les
  // royaumes et les utilisateurs du Keycloak, en silence.
  const [hostedApps, setHostedApps] = useState<Array<{ deployment_id: string; nom: string; type_application: string; label: string }>>([]);

  // Load restore operation or initiate if coming from backup list
  const loadData = useCallback(async () => {
    try {
      // If we have a restoreId in URL, load that restore directly
      if (restoreId && !restore) {
        const restoreData = await apiService.getRestoreStatus(deploymentId!, restoreId);
        setRestore(restoreData);

        // Also load the backup
        const backupData = await apiService.getBackup(deploymentId!, restoreData.backup_id);
        setBackup(backupData);
        setLoading(false);
        return;
      }

      // If we have a backupId but no restore yet, show confirmation modal
      if (backupId && !restore) {
        const backupData = await apiService.getBackup(deploymentId!, backupId);
        setBackup(backupData);
        // Best effort : un echec de resolution ne doit pas empecher la
        // restauration, seulement priver de l'avertissement.
        try {
          const details = await apiService.getDeploymentDetails(deploymentId!);
          setHostedApps(details.hosted_apps ?? []);
        } catch (e) {
          Logger.error('Impossible de resoudre les Keycloak heberges', e);
        }
        setShowConfirmModal(true);
        setLoading(false);
        return;
      }

      // If we have a restore operation in state, reload its status
      if (restore) {
        const updatedRestore = await apiService.getRestoreStatus(deploymentId!, restore.id);
        setRestore(updatedRestore);

        // Also load the backup
        const backupData = await apiService.getBackup(deploymentId!, updatedRestore.backup_id);
        setBackup(backupData);
      }
    } catch (error) {
      Logger.error('Error loading restore data:', error);
      toast.error(t('restore.loadError'));
    } finally {
      setLoading(false);
    }
  }, [deploymentId, backupId, restoreId, restore]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Poll status every 8 seconds if restore is in progress or pending
  useEffect(() => {
    if (!restore || restore.status === 'completed' || restore.status === 'failed' || restore.status === 'cancelled') {
      return;
    }

    const interval = setInterval(() => {
      loadData();
    }, 8000);

    return () => clearInterval(interval);
  }, [restore, loadData]);

  const handleInitiateRestore = async () => {
    try {
      setLoading(true);
      const initiatedRestore = await apiService.initiateRestore(
        deploymentId!,
        backupId!
      );
      setRestore(initiatedRestore);
      setShowConfirmModal(false);

      toast.success(t('restore.initiateSuccess'));

      // Navigate to the restore status page with restoreId
      navigate(`/deployments/${deploymentId}/restores/${initiatedRestore.id}`, { replace: true });
    } catch (error: unknown) {
      Logger.error('Error initiating restore:', error);
      toast.error(getApiErrorMessage(error) || t('restore.initiateError'));
    } finally {
      setLoading(false);
    }
  };

  const handleValidateRestore = async () => {
    try {
      setValidating(true);
      await apiService.validateRestore(deploymentId!, restore!.id, true);
      toast.success(t('restore.validateSuccess'));
      setShowValidateModal(false);
      loadData();
    } catch (error: unknown) {
      Logger.error('Error validating restore:', error);
      toast.error(getApiErrorMessage(error) || t('restore.validateError'));
    } finally {
      setValidating(false);
    }
  };

  const handleCancelRestore = async () => {
    if (!window.confirm(t('restore.cancelConfirm') as string)) {
      return;
    }

    try {
      setCancelling(true);
      await apiService.cancelRestore(deploymentId!, restore!.id);
      toast.success(t('restore.cancelSuccess'));
      navigate(`/deployments/${deploymentId}/backups`);
    } catch (error: unknown) {
      Logger.error('Error cancelling restore:', error);
      toast.error(getApiErrorMessage(error) || t('restore.cancelError'));
    } finally {
      setCancelling(false);
    }
  };

  if (loading && !restore) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/deployments/${deploymentId}/backups`)}
              className={`text-sm ${darkModeClasses.textSecondary} hover:${darkModeClasses.textPrimary} mb-4`}
            >
              {`\u2190 ${t('restore.backToBackups')}`}
            </button>
            <div className="flex items-start justify-between">
              <div>
                <h1 className={`text-3xl font-bold ${darkModeClasses.textPrimary}`}>{t('restore.title')}</h1>
                {restore && (
                  <div className="mt-3 flex items-center gap-3">
                    <RestoreStatusBadge status={restore.status} />
                    <span className={`text-sm ${darkModeClasses.textSecondary}`}>
                      ID: {restore.id.substring(0, 8)}...
                    </span>
                  </div>
                )}
              </div>
              {restore && (restore.status === 'pending' || restore.status === 'in_progress' || restore.status === 'awaiting_validation') && (
                <button
                  onClick={handleCancelRestore}
                  disabled={cancelling}
                  className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                    cancelling
                      ? 'bg-surface-3 text-text-tertiary cursor-not-allowed'
                      : 'bg-red-600 hover:bg-red-700 text-white'
                  }`}
                >
                  {cancelling ? t('restore.cancelling') : t('restore.cancelRestore')}
                </button>
              )}
            </div>
          </div>

          {backup && (
            <div className={`${darkModeClasses.card} p-6 mb-6`}>
              <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-4`}>{t('restore.backupInfo')}</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className={darkModeClasses.textSecondary}>{t('restore.backupName')}</span>
                  <p className={`${darkModeClasses.textPrimary} font-medium mt-1`}>{backup.backup_name}</p>
                </div>
                <div>
                  <span className={darkModeClasses.textSecondary}>{t('restore.creationDate')}</span>
                  <p className={darkModeClasses.textPrimary}>
                    {new Intl.DateTimeFormat('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }).format(new Date(backup.started_at))}
                  </p>
                </div>
              </div>
            </div>
          )}

          {restore && (
            <>
              {/* Timeline */}
              <div className={`${darkModeClasses.card} p-6 mb-6`}>
                <h2 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-6`}>{t('restore.progress')}</h2>
                <RestoreStatusTimeline restore={restore} />
              </div>

              {/* Action Button for Validation */}
              {restore.status === 'awaiting_validation' && (
                <div className={`${darkModeClasses.card} p-6`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-12 w-12 rounded-full bg-orange-500/20">
                        <svg className="h-6 w-6 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold ${darkModeClasses.textPrimary} mb-2`}>
                        {t('restore.validationRequired')}
                      </h3>
                      <p className={`${darkModeClasses.textSecondary} text-sm mb-4`}>
                        {t('restore.validationDescription')}
                      </p>
                      <button
                        onClick={() => setShowValidateModal(true)}
                        className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded text-sm font-medium transition-colors"
                      >
                        {t('restore.validateAndSwitch')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
      </div>

      {/* Confirmation Modal for Initial Restore */}
      {backup && showConfirmModal && (
        <RestoreConfirmModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            navigate(`/deployments/${deploymentId}/backups`);
          }}
          onConfirm={handleInitiateRestore}
          backup={backup}
          hostedApps={hostedApps}
          loading={loading}
        />
      )}

      {/* Validation Modal */}
      {showValidateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${darkModeClasses.card} max-w-lg w-full p-6`}>
            <h3 className={`text-xl font-bold ${darkModeClasses.textPrimary} mb-4`}>
              {t('restore.confirmSwitch')}
            </h3>
            <div className="bg-red-500/12 border border-red-500/20 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-400">
                <ExclamationTriangleIcon className="w-4 h-4 inline mr-1" />{t('restore.switchWarning')}
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowValidateModal(false)}
                disabled={validating}
                className="px-4 py-2 bg-surface-3 hover:bg-surface-3 text-text-primary rounded text-sm font-medium"
              >
                {t('restore.cancel')}
              </button>
              <button
                onClick={handleValidateRestore}
                disabled={validating}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  validating
                    ? 'bg-orange-500/30 text-orange-100 cursor-not-allowed'
                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                }`}
              >
                {validating ? t('restore.validating') : t('restore.confirmSwitchBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RestorePage;
